import Modal from "common/Modal/Modal";
import React from "react";
import Button, { buttonStyles } from "common/Buttons/Button";
import styled from "styled-components";
import Steps from "common/Steps/Steps";
import {
  ConnectGroupModalProps,
  ConnectGroupModalSteps,
} from "@shared/features/newMarketplace/types";
import useConnectGroupModal from "@shared/features/newMarketplace/hooks/useConnectGroupModal";
import { openLink, openMessage } from "utils/misc";
import Text from "common/Typography/Text";
import { useHistory } from "react-router-dom";
import { appendSubGroupId, getCreateBotRoute } from "utils/routes";
import { BotType, LanguageNameSpaces } from "@shared/types";
import ConnectGroup from "features/newMarketplace/GroupExtraDetails/JoinGroup/ConnectGroup";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const ConnectGroupModal = ({
  visible,
  closeModal,
  groupData,
  modalData,
  isHighRisk,
}: ConnectGroupModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const history = useHistory();

  const {
    showError,
    onClickContinue,
    isChecked,
    setIsChecked,
    onCloseModal,
    step,
    showSteps,
  } = useConnectGroupModal(modalData, closeModal, openLink, openMessage);

  const connectGroup = (
    <div style={{ padding: 24 }}>
      <ConnectGroup
        content={modalData.modalContent}
        setIsChecked={setIsChecked}
        showError={showError}
        isHighRisk={isHighRisk}
      />
    </div>
  );

  const steps = [
    {
      title: t("group_info.modals.connect_group"),
      content: connectGroup,
    },
    {
      title: t("group_info.modals.auto_trading"),
      content: (
        <div style={{ padding: 24 }}>
          <Text>{t("group_info.modals.create_signals_bot_message")}</Text>
        </div>
      ),
    },
  ];

  const connectGroupFooter = (
    <>
      <Button onClick={onCloseModal}>{t("group_info.modals.cancel")}</Button>
      <StyledButton
        type="primary"
        loading={modalData.loading}
        onClick={onClickContinue}
        $disabled={!isChecked}
      >
        {modalData.buttonText}
      </StyledButton>
    </>
  );

  const onCreateSignalsBotClick = () => {
    if (groupData?.id) {
      const path = appendSubGroupId(
        getCreateBotRoute(BotType.SIGNAL),
        groupData?.id
      );
      history.push(path);
    }
  };

  const createSignalsBotFooter = (
    <>
      <Button onClick={onCloseModal}>
        {t("group_info.modals.skip_for_now")}
      </Button>
      <Button type="primary" onClick={onCreateSignalsBotClick}>
        {t("group_info.modals.create_signals_bot")}
      </Button>
    </>
  );

  const getFooter = () => {
    if (step === ConnectGroupModalSteps.CONNECT_GROUP)
      return connectGroupFooter;
    return createSignalsBotFooter;
  };

  const items = steps.map((step) => ({ title: step.title }));

  return (
    <Modal
      title={modalData.modalTitle}
      open={visible}
      onCancel={onCloseModal}
      destroyOnHidden={true}
      width={600}
      footer={getFooter()}
      centered={true}
      styles={{
        body: {
          maxHeight: 575,
          overflow: "auto",
          padding: 0,
        },
      }}
    >
      {showSteps ? (
        <>
          <Steps
            current={step}
            items={items}
            style={{ padding: "24px 24px 0" }}
          />
          <div>{steps[step].content}</div>
        </>
      ) : (
        connectGroup
      )}
    </Modal>
  );
};

export default ConnectGroupModal;

const StyledButton = styled(Button)<{ $disabled: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
    ${buttonStyles.antBtnDisabled};
    `}
`;
